import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { FormInput } from "../../../components/common/FormInput";
import { SimpleDropdown } from "../../../components/common/SimpleDropdown";
import { PLAYER_FIELDS } from "../../../components/admin/dashboard/playerFields";
import { COACH_FIELDS } from "../../../components/admin/dashboard/coachFields";
import { updateUserProfile, setManualEditsFilters } from "../../../features/admin/adminSlice";
import adminService from "../../../services/adminService";

const mapUserToFormData = (user, fields) => {
  if (!user) return {};
  const mapped = {};
  fields.forEach((field) => {
    const key = field.key;
    let value = "";
    if (key === "teamName") {
      value = user.team?.name || "";
    } else if (key === "email") {
      value = user.email ?? "";
    } else if (key === "seasonYear") {
      value = user.seasonYear || "2025-26";
    } else {
      value = user[key] ?? "";
    }
    mapped[key] = value === null ? "" : String(value);
  });
  return mapped;
};

export const ManualEditsProfileForm = ({ userData }) => {
  const dispatch = useDispatch();
  const role = userData?.role?.toLowerCase() || "player";
  const isPlayer = role === "player";
  const fields = isPlayer ? PLAYER_FIELDS : COACH_FIELDS;

  const [formData, setFormData] = useState({});
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoUploadMsg, setVideoUploadMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [videos, setVideos] = useState([]);
  const [deletingVideoId, setDeletingVideoId] = useState(null);
  const [playingVideoId, setPlayingVideoId] = useState(null);
  const [committedToError, setCommittedToError] = useState("");

  useEffect(() => {
    if (userData) {
      const mapped = mapUserToFormData(userData, fields);
      mapped.committedTo = userData.committedTo || "";
      setFormData(mapped);
      setVideoUploadMsg("");
      setVideos(userData.videos || []);
      setPlayingVideoId(null);
      setCommittedToError("");
    } else {
      setFormData({});
      setVideos([]);
    }
  }, [userData]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    if (!userData?._id) return;
    if (formData.commitmentStatus === "committed" && !formData.committedTo?.trim()) {
      setCommittedToError("'Committed To' is required when status is committed");
      return;
    }
    setCommittedToError("");
    setSaving(true);
    try {
      const payload = { ...formData };
      delete payload.teamName;
      delete payload.ncaaId;
      if (payload.commitmentStatus !== "committed") {
        payload.committedTo = "";
      }
      if (payload.previousTeams !== undefined) {
        payload.previousTeams = payload.previousTeams
          ? payload.previousTeams.split(",").map((t) => t.trim()).filter(Boolean)
          : [];
      }
      await dispatch(updateUserProfile({ id: userData._id, profileData: payload })).unwrap();
      dispatch(setManualEditsFilters({ page: 1 }));
    } catch (error) {
      console.error("Failed to save changes:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !userData?._id) return;
    setVideoUploading(true);
    setVideoUploadMsg("");
    try {
      const res = await adminService.uploadUserVideo(userData._id, file);
      const newVideos = res?.videos || res?.data?.videos;
      if (newVideos) setVideos(newVideos);
      setVideoUploadMsg("Video uploaded successfully!");
    } catch (err) {
      setVideoUploadMsg(err.response?.data?.message || "Failed to upload video");
    } finally {
      setVideoUploading(false);
      e.target.value = "";
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!userData?._id) return;
    setDeletingVideoId(videoId);
    try {
      await adminService.deleteUserVideo(userData._id, videoId);
      setVideos((prev) => prev.filter((v) => v._id !== videoId));
      if (playingVideoId === videoId) setPlayingVideoId(null);
    } catch (err) {
      setVideoUploadMsg(err.response?.data?.message || "Failed to delete video");
    } finally {
      setDeletingVideoId(null);
    }
  };

  if (!userData) {
    return (
      <div className="rounded-2xl bg-jp-card border border-jp-line p-6 flex items-center justify-center">
        <p className="text-jp-muted text-sm">Select a user to edit</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl flex flex-col gap-3">
      <div className="bg-jp-card border border-jp-line rounded-2xl p-4 flex flex-col gap-4 max-h-[600px] overflow-y-auto">
        {/* Profile header */}
        <div className="flex items-center gap-3">
          <img
            src={userData.profileImage || userData.photoUrl || ""}
            alt=""
            className="w-14 h-14 rounded-lg object-cover bg-jp-section"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div>
            <h3 className="text-sm font-semibold text-jp-ink">
              {userData.firstName} {userData.lastName}
            </h3>
            <p className="text-[11px] text-jp-muted">
              {userData.team?.name || ""} {userData.position ? `· ${userData.position}` : ""}
            </p>
          </div>
        </div>

        {/* Form fields - same as PlayerCoachManualEditModal */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          {fields.map((field, idx) => (
            <React.Fragment key={`${field.key}-${idx}`}>
              <div className={field.input === "Textarea" ? "md:col-span-2" : ""}>
                {field.input === "Select" ? (
                  <div>
                    <label className="block text-left text-jp-ink text-sm font-roboto mb-3">{field.field}</label>
                    <SimpleDropdown
                      variant="form"
                      value={formData[field.key] || ""}
                      onChange={(val) => {
                        setFormData({ ...formData, [field.key]: val });
                        if (field.key === "commitmentStatus") setCommittedToError("");
                      }}
                      options={field.options.map((opt) => ({ value: opt, label: opt }))}
                      placeholder="Select..."
                    />
                  </div>
                ) : (
                  <FormInput
                    label={field.field}
                    name={field.key}
                    type={field.input === "Number" ? "number" : field.input === "Email Input" ? "email" : "text"}
                    value={formData[field.key] || ""}
                    onChange={handleChange}
                    placeholder={field.example}
                  />
                )}
              </div>

              {field.key === "commitmentStatus" && formData.commitmentStatus === "committed" && (
                <div>
                  <FormInput
                    label="Committed To *"
                    name="committedTo"
                    type="text"
                    value={formData.committedTo || ""}
                    onChange={handleChange}
                    placeholder="e.g. University of Texas"
                  />
                  {committedToError && (
                    <p className="text-red-600 dark:text-red-400 text-xs mt-1">{committedToError}</p>
                  )}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Video Upload Section - Only for Players */}
        {isPlayer && (
          <div className="pt-3 border-t border-jp-line">
            <label className="block text-jp-ink text-xs font-medium mb-2">Upload Video</label>

            {videos.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-2">
                {videos.map((video, index) => (
                  <div key={video._id || index} className="relative aspect-video bg-jp-section rounded-lg overflow-hidden group">
                    {playingVideoId === video._id ? (
                      <video
                        src={video.url || video.videoUrl}
                        controls
                        autoPlay
                        className="w-full h-full object-cover"
                        onEnded={() => setPlayingVideoId(null)}
                      />
                    ) : (
                      <>
                        {video.thumbnailUrl ? (
                          <img src={video.thumbnailUrl} alt="Video thumbnail" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-jp-section" />
                        )}
                        <button
                          type="button"
                          onClick={() => setPlayingVideoId(video._id)}
                          className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <svg className="w-8 h-8 text-white drop-shadow-lg" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M6 4l8 6-8 6V4z"/>
                          </svg>
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDeleteVideo(video._id)}
                      disabled={deletingVideoId === video._id}
                      className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-600/80 hover:bg-red-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:cursor-not-allowed"
                    >
                      {deletingVideoId === video._id ? (
                        <div className="w-2.5 h-2.5 border border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}

            <label className={`inline-flex items-center justify-center px-4 py-2 rounded-lg font-semibold text-[10px] uppercase tracking-wide transition-all shadow-lg ${
              videoUploading
                ? 'bg-jp-section text-jp-muted cursor-not-allowed'
                : 'bg-cyan-500 text-white hover:bg-cyan-400 cursor-pointer'
            }`}>
              <input
                type="file"
                accept="video/*"
                onChange={handleVideoUpload}
                className="hidden"
                disabled={videoUploading}
              />
              {videoUploading && (
                <div className="w-3 h-3 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              )}
              {videoUploading ? 'UPLOADING...' : 'UPLOAD VIDEO'}
            </label>

            {videoUploadMsg && (
              <p className={`mt-1 text-[10px] ${videoUploadMsg.includes('success') ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {videoUploadMsg}
              </p>
            )}
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-center pt-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="min-w-[140px] h-9 rounded-full bg-sky-500 text-[11px] font-semibold text-white hover:bg-sky-400 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'SAVING...' : 'SAVE CHANGES'}
          </button>
        </div>
      </div>
    </div>
  );
};
